import styled, { keyframes } from 'styled-components';
import { Card, Input, Spin } from 'antd';
import { Avatar } from 'antd';

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

export const ProductContainer = styled.div`
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 24px;
    padding-bottom: 150px;
    min-height: calc(100vh - 120px);
    animation: ${fadeIn} 0.3s ease-in-out;

    @media (max-width: 768px) {
        padding: 16px;
        padding-bottom: 120px;
    }
`;

export const SearchContainer = styled.div`
    margin-bottom: 32px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const SearchInput = styled(Input)`
    height: 48px;
    padding: 8px 16px;
    border-radius: 12px;
    font-size: 16px;
    border: 2px solid #f0f0f0;
    transition: all 0.3s ease;
    
    &:hover {
        border-color: #d9d9d9;
    }
    
    &:focus,
    &:active {
        border-color: #1890ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }

    .ant-input-prefix {
        color: #bfbfbf;
        margin-right: 8px;
    }

    .ant-input-clear-icon {
        color: #bfbfbf;
        
        &:hover {
            color: #595959;
        }
    }
`;

export const ProductCount = styled.div`
    color: #8c8c8c;
    font-size: 14px;
    margin-left: 4px;
`;

export const CategoryContainer = styled.div`
    margin-bottom: 48px;
    animation: ${fadeIn} 0.3s ease-in-out;

    &:last-child {
        margin-bottom: 0;
    }
`;

export const CategoryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    span {
        color: #8c8c8c;
        font-size: 14px;
    }
`;

export const ProductGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 24px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 16px;
    }
`;

export const ProductCard = styled(Card)`
    border-radius: 16px;
    border: 1px solid #f0f0f0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: #ffffff;
    overflow: hidden;
    height: 100%;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
        border-color: #e6f7ff;
    }

    .ant-card-body {
        padding: 20px;
    }
`;

export const ProductIcon = styled(Avatar)`
    && {
        width: 64px;
        height: 64px;
        border-radius: 12px;
        background: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }
`;

export const ProductMeta = styled(Card.Meta)`
    .ant-card-meta-detail {
        margin-left: 16px;
    }

    .ant-card-meta-title {
        margin-bottom: 8px;
    }
`;

export const ProductTitle = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    margin: 0;
    line-height: 1.4;
    display: flex;
    align-items: center;
    gap: 8px;

    .product-tag {
        font-size: 12px;
        font-weight: 500;
        padding: 2px 8px;
        border-radius: 4px;
        background: #e6f7ff;
        color: #1890ff;
    }
`;

export const ProductDescription = styled.p`
    font-size: 14px;
    color: #595959;
    margin: 4px 0 0;
    line-height: 1.6;
`;

export const SectionTitle = styled.h3`
    font-size: 24px;
    font-weight: 700;
    color: #262626;
    margin: 0;
    position: relative;
    display: inline-block;
    
    &:after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 0;
        width: 40px;
        height: 3px;
        background: #1890ff;
        border-radius: 2px;
    }
`;

export const EmptyState = styled.div`
    text-align: center;
    padding: 64px 24px;
    background: #fafafa;
    border-radius: 16px;
    margin-top: 32px;
    
    h3 {
        font-size: 20px;
        font-weight: 600;
        color: #262626;
        margin-bottom: 8px;
    }
    
    p {
        color: #8c8c8c;
        font-size: 16px;
        margin: 0;
    }
`;

// Loading Spinner
export const LoadingSpinner = styled(Spin)`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    width: 100%;

    .ant-spin-dot {
        font-size: 24px;
    }

    .ant-spin-dot-item {
        background-color: #1890ff;
    }

    .ant-spin-text {
        margin-top: 8px;
        color: #8c8c8c;
        font-size: 14px;
    }
`;

// Badge Component
export const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    margin-left: 8px;
    
    &.new {
        background: #e6f7ff;
        color: #1890ff;
    }
    
    &.popular {
        background: #f6ffed;
        color: #52c41a;
    }
    
    &.bestseller {
        background: #fff7e6;
        color: #fa8c16;
    }
`;

// Tooltip Components
export const TooltipContainer = styled.div`
    position: relative;
    display: inline-block;

    &:hover .tooltip {
        visibility: visible;
        opacity: 1;
        transform: translateY(-5px);
    }
`;

export const Tooltip = styled.div`
    visibility: hidden;
    opacity: 0;
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%) translateY(0);
    padding: 8px 12px;
    background: rgba(0, 0, 0, 0.85);
    color: white;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    &:after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: rgba(0, 0, 0, 0.85) transparent transparent transparent;
    }
`;

// Responsive breakpoints
export const breakpoints = {
    xs: '480px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    xxl: '1600px'
};

// Theme colors
export const themeColors = {
    primary: '#1890ff',
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
    text: {
        primary: '#262626',
        secondary: '#595959',
        disabled: '#bfbfbf'
    },
    background: {
        light: '#ffffff',
        base: '#f0f2f5',
        dark: '#001529'
    },
    border: {
        base: '#f0f0f0',
        split: '#d9d9d9'
    }
};

// Common mixins
export const commonMixins = {
    cardShadow: `
        box-shadow: 0 1px 2px -2px rgba(0, 0, 0, 0.16),
                    0 3px 6px 0 rgba(0, 0, 0, 0.12),
                    0 5px 12px 4px rgba(0, 0, 0, 0.09);
    `,
    transition: `
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    `,
    flexCenter: `
        display: flex;
        align-items: center;
        justify-content: center;
    `,
    textTruncate: `
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    `,
    scrollbar: `
        &::-webkit-scrollbar {
            width: 6px;
            height: 6px;
        }
        
        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
        }
        
        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 3px;
            
            &:hover {
                background: #555;
            }
        }
    `
};